import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Upload, Loader2, MessageCircle } from "lucide-react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../../config";
import { useTheme } from "../../../contexts/ThemeContext";

const SendMessageModal = ({
  selectedMembers,
  setIsSendMessageModalOpen,
  personalizedMessage,
  setPersonalizedMessage,
  files,
  setFiles,
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [fileUrls, setFileUrls] = useState([]);

  useEffect(() => {
    console.log("Selected Members:", selectedMembers);
    console.log("Selected Files:", files);
  }, [selectedMembers, files]);

  const handleSendMessage = async () => {
    const token = window.__appSession.getToken();
    setLoading(true);

    if (!selectedMembers || selectedMembers.length === 0) {
      toast.error("No members selected.");
      setLoading(false);
      return;
    }

    const mobileNumbers = selectedMembers.map((member) => member);
    const message = personalizedMessage ? [personalizedMessage] : [];

    let fileUrls = [];
    if (files && files.length > 0) {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append("files", file));

      try {
        const uploadResponse = await axios.post(
          `${API_BASE_URL}/api/whatsapp/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (
          uploadResponse &&
          uploadResponse.data &&
          uploadResponse.data.filePathUrls
        ) {
          fileUrls = uploadResponse.data.filePathUrls;
          console.log("Uploaded File URLs:", fileUrls);
        } else {
          toast.error("No file URLs returned from the server.");
          setLoading(false);
          return;
        }
      } catch (error) {
        toast.error("Error uploading files.");
        setLoading(false);
        return;
      }
    }

    try {
      // Use the send-message2 endpoint which accepts an array of recipient IDs
      // This ensures messages are sent to all selected recipients
      const response = await axios.post(
        `${API_BASE_URL}/api/whatsapp/send-message2`,
        {
          recipientIds: mobileNumbers, // send as array
          message: message,
          filePathUrl: fileUrls.length > 0 ? fileUrls : [],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Message sent successfully.");
      setIsSendMessageModalOpen(false);
      setPersonalizedMessage("");
    } catch (error) {
      // If send-message2 fails, fall back to legacy single-endpoint behaviour
      console.error("send-message2 failed, attempting fallback:", error);
      try {
        await axios.post(
          `${API_BASE_URL}/api/whatsapp/send-message`,
          {
            receiverMobileNo: mobileNumbers.join(","),
            message: message,
            filePathUrl: fileUrls.length > 0 ? fileUrls : [],
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success("Message sent successfully (fallback).");
        setIsSendMessageModalOpen(false);
        setPersonalizedMessage("");
      } catch (err) {
        toast.error("Error sending the message.");
        console.error("Fallback send failed:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = e.target.files;
    setFiles(selectedFiles);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className={`${theme.contentBg} ${theme.contentBorder}  rounded-xl shadow-2xl w-full max-w-md`}
        >
          {/* Header */}
          <div
            className={`${theme.grpBG} ${theme.headerBorder} border-b px-6 py-4 rounded-t-xl`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`${theme.logoBG} p-2 rounded-full`}>
                  <MessageCircle className={`w-5 h-5 ${theme.iconColor}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${theme.headerText}`}>
                    Send Message
                  </h3>
                  <p className={`text-xs ${theme.headerText} opacity-75`}>
                    Message {selectedMembers.length} member(s)
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsSendMessageModalOpen(false)}
                className={`${theme.sidebarText} opacity-60 hover:opacity-100 transition-opacity`}
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Message Input */}
            <div className="space-y-2">
              <label className={`text-sm font-medium ${theme.sidebarText}`}>
                Message
              </label>
              <motion.textarea
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                value={personalizedMessage}
                onChange={(e) => setPersonalizedMessage(e.target.value)}
                placeholder="Type your message here..."
                className={`w-full p-3 border ${theme.sidebarBorder} rounded-lg ${theme.sidebarBg} ${theme.sidebarText} ${theme.inputFocusRing} focus:border-transparent focus:ring-2 resize-none transition-all duration-200`}
                rows="4"
              />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <label className={`text-sm font-medium ${theme.sidebarText}`}>
                Attachments
              </label>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`border-2 border-dashed ${theme.sidebarBorder} rounded-lg p-4 text-center ${theme.sidebarBg} hover:border-opacity-70 transition-colors`}
              >
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className={`cursor-pointer flex flex-col items-center space-y-2 ${theme.sidebarText}`}
                >
                  <Upload className="w-8 h-8 opacity-50" />
                  <span className="text-sm">Click to upload files</span>
                  {files && files.length > 0 && (
                    <span className="text-xs text-green-600">
                      {files.length} file(s) selected
                    </span>
                  )}
                </label>
              </motion.div>
            </div>
          </div>

          {/* Footer */}
          <div
            className={`${theme.sidebarBg} ${theme.sidebarBorder} border-t px-6 py-4 rounded-b-xl`}
          >
            <div className="flex justify-end space-x-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsSendMessageModalOpen(false)}
                className={`px-4 py-2 ${theme.sidebarText} opacity-60 hover:opacity-100 transition-opacity rounded-lg text-sm font-medium`}
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSendMessage}
                disabled={loading}
                className={`px-4 py-2 ${theme.accentColor} ${theme.buttonHover} text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SendMessageModal;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { ToastContainer, toast } from "react-toastify";
// import 'react-toastify/dist/ReactToastify.css';
// import { API_BASE_URL } from '../../../config';
// const SendMessageModal = ({ selectedMembers, setIsSendMessageModalOpen, personalizedMessage, setPersonalizedMessage, files, setFiles }) => {
//   const [loading, setLoading] = useState(false);
//   const [fileUrls, setFileUrls] = useState([]);

//   useEffect(() => {
//     console.log("Selected Members:", selectedMembers);
//     console.log("Selected Files:", files);
//   }, [selectedMembers, files]);

//   const handleSendMessage = async () => {
//     const token = window.__appSession.getToken();
//     setLoading(true);

//     // Check if selectedMembers is populated correctly
//     if (!selectedMembers || selectedMembers.length === 0) {
//       toast.error("No members selected.");
//       setLoading(false);
//       return;
//     }

//     // Prepare the receiver's mobile numbers (which are the member IDs with @c.us)
//     const mobileNumbers = selectedMembers.map(member => member);  // Since member.id is already in the correct format

//     // Ensure the message is an array
//     const message = personalizedMessage ? [personalizedMessage] : [];

//     // Upload files first if any
//     let fileUrls = [];  // Initialize fileUrls here
//     if (files && files.length > 0) {
//       const formData = new FormData();

//       // Convert FileList to an array and append files to FormData
//       Array.from(files).forEach(file => formData.append('files', file));

//       try {
//         const uploadResponse = await axios.post(
//           `${API_BASE_URL}/api/whatsapp/upload`,  // Make sure this endpoint is correct
//           formData,
//           {
//             headers: {
//               "Content-Type": "multipart/form-data",
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );

//         // Check if file URLs are returned in the response
//         if (uploadResponse && uploadResponse.data && uploadResponse.data.filePathUrls) {
//           fileUrls = uploadResponse.data.filePathUrls;  // Directly set fileUrls here
//           console.log("Uploaded File URLs:", fileUrls); // Log the file URLs for debugging
//         } else {
//           toast.error('No file URLs returned from the server.');
//           setLoading(false);
//           return;
//         }
//       } catch (error) {
//         toast.error('Error uploading files.');
//         setLoading(false);
//         return;
//       }
//     }

//     // Now send the message
//     try {
//       const response = await axios.post(
//         `${API_BASE_URL}/api/whatsapp/send-message`,  // Make sure this endpoint is correct
//         {
//           receiverMobileNo: mobileNumbers.join(', '), // Join the mobile numbers with a comma
//           message: message, // Send the message as an array of strings
//           filePathUrl: fileUrls.length > 0 ? fileUrls : [], // Ensure the file URL is an array
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       toast.success('Message sent successfully.');
//       setIsSendMessageModalOpen(false);  // Close the modal on success
//       setPersonalizedMessage("");
//     } catch (error) {
//       toast.error('Error sending the message.');
//       console.error('Error sending message:', error);  // Log the error for debugging
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Handle file input changes
//   const handleFileChange = (e) => {
//     const selectedFiles = e.target.files;
//     setFiles(selectedFiles);  // Set the FileList directly to state
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
//       <div className="bg-white p-6 rounded-lg w-96">
//         <h3 className="text-lg font-semibold text-indigo-600 mb-4">Send Message</h3>

//         {/* Message Input */}
//         <textarea
//           value={personalizedMessage}
//           onChange={(e) => setPersonalizedMessage(e.target.value)}
//           placeholder="Type your message here"
//           className="w-full p-2 border border-gray-300 rounded-lg mb-4"
//           rows="4"
//         ></textarea>

//         {/* File Upload */}
//         <input
//           type="file"
//           multiple
//           onChange={handleFileChange}
//           className="w-full mb-4"
//         />

//         <div className="flex justify-end space-x-4">
//           <button
//             onClick={() => setIsSendMessageModalOpen(false)}
//             className="bg-gray-400 text-white py-2 px-4 rounded-md"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleSendMessage}
//             className="bg-indigo-600 text-white py-2 px-4 rounded-md"
//             disabled={loading}
//           >
//             {loading ? 'Sending...' : 'Send'}
//           </button>
//         </div>
//       </div>
//       <ToastContainer />
//     </div>
//   );
// };

// export default SendMessageModal;
